import styled from 'styled-components';

import { A, FocusButton, standardFocusStyles } from '@main/pc-core-components';
import { responsive, sp, timing } from '@main/theme';

import type { NavMode } from './types';

// To cover the global mobile Take Control button on bottom of screen
const fullPageWrapperZIndex = 3000;

/**
 * The FullPage Wrapper
 */
export const FullPageWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${({ theme }) => theme.colors.white};
  height: 100%;
  width: 100%;
  overflow: auto;
  z-index: ${fullPageWrapperZIndex};

  ul li {
    list-style-type: none;
  }
`;

// Pull this above other 'position: !relative' elements on the page

/**
 * Styled focus button for consistent focus outline
 */
export const NavToggleIcon = styled(FocusButton)`
  display: block;

  span,
  span > i {
    display: block;
  }
`;

/**
 * The Links Wrapper
 */
export const LinksWrapper = styled.nav`
  padding: ${sp(3)};
  margin-top: ${sp(10)};

  span {
    border-bottom: none;
  }

  ul {
    padding-left: 36px;
  }

  li {
    margin-bottom: ${sp(2)};
    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    transition: all 0.2s;
    padding-top: 5px;
    padding-bottom: 5px;

    &:focus {
      border-color: transparent;
      border-radius: ${sp(0.5)};
      opacity: 1;
      ${standardFocusStyles}
    }
  }
`;

/**
 * Msg wrapper props
 */
export interface MsgWrapperProps {
  /** Is selected */
  selected?: boolean;
  /** Mode */
  mode?: NavMode;
}

/**
 * The Msg wrapper
 * Necessary to underline just the text, not the whole row.
 */
export const MsgWrapper = styled.span<MsgWrapperProps>`
  ${({ mode, theme, selected }) =>
    mode === 'fullpage'
      ? `
        color: ${theme.colors.textOnBg};
        padding-bottom: ${sp(0.5)};
        border-bottom: 2px solid ${
          selected ? theme.colors.textOnPrimary : 'transparent'
        };
        `
      : ''}// mode === bar
`;

/**
 * The NavBar wrapper
 */
export const NavBarWrapper = styled.div<{
  /** Whether to hide the tabs or not if there's only one page */
  _hidden?: boolean;
}>`
  display: none;

  ${responsive.atLeastTablet} {
    display: flex;
    ${({ _hidden }) => _hidden && 'visibility: hidden;'}
    flex-direction: row;
    justify-content: center;
  }
`;

/**
 * Wrapper for nav menu
 */
export const NavMenuWrapper = styled.div``;

/**
 * NavToggle props
 */
export interface NavToggleProps {
  /** Open or closed */
  isOpened?: boolean;
}

/**
 * The NavToggle wrapper
 */
export const NavToggleWrapper = styled.div<NavToggleProps>`
  ${responsive.beforeTablet} {
    cursor: pointer;
    opacity: 0.75;
    &:hover {
      opacity: 1;
    }
    /* Increase the hitbox to make it easier to click on mobile */
    padding: ${sp(1)};
    margin: ${sp(-1)};
  }
  ${responsive.atLeastTablet} {
    display: none;
  }

  svg {
    fill: ${({ theme }) => theme.colors.primary};
    font-size: ${sp(3)};
  }

  position: relative;
  z-index: ${fullPageWrapperZIndex + 1};
`;

/**
 * The NavMenu wrapper
 */
export const StyledA = styled(A)<{
  /** The menu mode */
  mode?: NavMode;
}>`
  // Remove default ant styling
  background-color: transparent;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textOnBg};
  font-family: '${({ theme }) => theme.textStyles.fontFamilyHeader.name}';
  font-weight: 400;
  opacity: 0.65;
  border-radius: 0;
  margin: 0 ${sp(1)} 0 ${sp(1)};
  padding: 0 ${sp(1)} 0 ${sp(1)};
  &:focus {
    text-decoration: none;
  }
  &:active,
  &:hover {
    outline: 0;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.textOnBg};
  }

  // Shared styles
  transition: ${timing.shortEase(
    'color',
    'border-bottom-color',
    'border-top-color',
  )};

  ${({ mode, theme, selected }) =>
    mode === 'fullpage'
      ? `
        -moz-transition: opacity 0.2s ease-in-out;
        -webkit-transition: opacity 0.2s ease-in-out;
        -o-transition: opacity 0.2s ease-in-out;
        transition: opacity 0.2s ease-in-out;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        display: block;
        margin-top: ${sp(2)};
        &:first-child { margin-top: 0; }
        color: ${theme.colors.textOnBg};
        font-size: ${sp(3)};
        font-weight: ${selected ? '600' : '400'};
        opacity: ${selected ? '1' : '0.65'};

        border-left: 5px solid ${
          selected ? theme.colors.primary : 'transparent'
        };
        padding: 0 0 0 ${sp(3)};
        &:hover {
          color: ${
            theme.colors.textOnBg
          }; // Necessary b/c ant sets this globally
          opacity: 1;
        }
        &:active {
          color: ${
            theme.colors.textOnBg
          }; // Necessary b/c ant sets this globally
        }
        `
      : ` // mode === bar
        display: inline-block;
        padding: ${sp()} ${sp(2)};
        opacity: ${selected ? '1' : '0.65'};
        height: ${sp(10)};

        font-size: inherit;
        text-align: center;
        display: flex;
        align-items: center;

        font-weight: ${selected ? '600' : '400'};
        -moz-transition: all 0.2s ease-in-out;
        -webkit-transition: all 0.2s ease-in-out;
        -o-transition: all 0.2s ease-in-out;
        transition: all 0.2s ease-in-out;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;

        border-bottom: 3.5px solid ${
          selected ? theme.colors.primary : 'transparent'
        };
        margin-bottom: -1px; // Overlap the link bottom border on the navbar's bottom border

        &:hover {
          opacity: 1;
        }

        &:focus {
          opacity: 1;
          border-radius: 4px;
          border-color: ${theme.colors.white};
          box-shadow: ${theme.colors.lightPrimary} 0 0 1px 3px,
          ${theme.colors.lightPrimary} 0 0 0 1px,
          ${theme.colors.lightPrimary} 0 2px 6px 0,
          ${theme.colors.lightPrimary} 0 1px 3.5px 0;
        }
        `}
`;
